import type { RegisteredComponent } from "@builder.io/sdk-react";
import { isEditing, isPreviewing } from "@builder.io/sdk-react";

import type { ClaimCardTheme } from "~/hec/components/molecules/claim-card/types";
import { InsidersClaimForm } from "~/hec/components/organisms/insiders-claim-form/insiders-claim-form";
import type {
  InsidersClaimCopy,
  InsidersClaimEditorState,
} from "~/hec/components/organisms/insiders-claim-form/types";
import { isClaimVariant } from "~/hec/loyalty/insiders-claim.schemas";

import { insidersClaimFormInputs } from "./inputs";

/**
 * COM-506 — the Insiders Perks points claim card as an authored block. Thin
 * wrapper: maps Builder inputs onto the `InsidersClaimForm` organism and
 * spreads `attributes` on the root (`noWrap` contract, CLAUDE.md §4). Page-body
 * block only — not registered for any chrome surface.
 *
 * `name: "InsidersClaimForm"` is the contract published content references;
 * it cannot be renamed.
 */
interface BuilderInsidersClaimFormProps extends InsidersClaimCopy {
  variant?: string;
  theme?: ClaimCardTheme;
  editorState?: InsidersClaimEditorState;
  attributes?: Record<string, unknown> & { className?: string };
}

const BuilderInsidersClaimForm = ({
  variant,
  theme,
  eyebrow,
  heading,
  body,
  buttonLabel,
  inputLabel,
  yesLabel,
  noLabel,
  editorState = "ready",
  attributes,
}: BuilderInsidersClaimFormProps) => {
  const { className, ...rest } = attributes ?? {};
  const inEditor = isEditing() || isPreviewing();
  return (
    <InsidersClaimForm
      // Unknown values never reach SFRA: an unknown `type` wipes the shopper's
      // stored claim history server-side.
      variant={isClaimVariant(variant) ? variant : "monthly"}
      theme={theme}
      copy={{
        eyebrow,
        heading,
        body,
        buttonLabel,
        inputLabel,
        yesLabel,
        noLabel,
      }}
      editorState={inEditor ? editorState : undefined}
      className={className}
      {...rest}
    />
  );
};

export const InsidersClaimFormDefinition = {
  component: BuilderInsidersClaimForm,
  name: "InsidersClaimForm",
  friendlyName: "Insiders Perks claim card (FSA + HSA)",
  noWrap: true,
  canHaveChildren: false,
  image: "https://unpkg.com/lucide-static@0.537.0/icons/gift.svg",
  inputs: insidersClaimFormInputs,
} satisfies RegisteredComponent;

export default BuilderInsidersClaimForm;
